import { m } from '@aimfora/i18n'
import { Button } from '@aimfora/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { DownloadIcon, FileTextIcon } from 'lucide-react'
import { GradingPreview } from '~/components/grading-preview'
import { LegalLinks } from '~/components/legal-page'
import { Logo } from '~/components/logo'
import { QuoteCarousel } from '~/components/quote-carousel'
import { RedactionFlow } from '~/components/redaction-flow'
import { getPublicConfig } from '~/functions/session.functions'
import { TRIAL_CREDITS } from '~/lib/billing'

export const Route = createFileRoute('/')({
  loader: () => getPublicConfig(),
  head: () => ({ meta: [{ title: `${m.forside_title()} – ${m.app_name()}` }] }),
  component: LandingPage,
})

const steps = () => [
  { title: m.forside_step_1_title(), body: m.forside_step_1_body() },
  { title: m.forside_step_2_title(), body: m.forside_step_2_body() },
  { title: m.forside_step_3_title(), body: m.forside_step_3_body() },
  { title: m.forside_step_4_title(), body: m.forside_step_4_body() },
]

const safeguards = () => [
  { title: m.forside_safe_1_title(), body: m.forside_safe_1_body() },
  { title: m.forside_safe_5_title(), body: m.forside_safe_5_body() },
  { title: m.forside_safe_3_title(), body: m.forside_safe_3_body() },
  { title: m.forside_safe_4_title(), body: m.forside_safe_4_body() },
]

const exampleSteps = () => [
  m.forside_example_step_1(),
  m.forside_example_step_2(),
  m.forside_example_step_3(),
  m.forside_example_step_4(),
  m.forside_example_step_5(),
]

const exampleFiles = () => [
  {
    file: 'opgavesaet.pdf',
    name: m.forside_file_exam_name(),
    note: m.forside_file_exam_note(),
  },
  {
    file: 'rettevejledning.pdf',
    name: m.forside_file_guide_name(),
    note: m.forside_file_guide_note(),
  },
  {
    file: 'omsaetningstabel.pdf',
    name: m.forside_file_table_name(),
    note: m.forside_file_table_note(),
  },
  {
    file: 'besvarelse-elev-1.pdf',
    name: m.forside_file_student_1_name(),
    note: m.forside_file_student_1_note(),
  },
  {
    file: 'besvarelse-elev-2.pdf',
    name: m.forside_file_student_2_name(),
    note: m.forside_file_student_2_note(),
  },
  {
    file: 'besvarelse-elev-3.pdf',
    name: m.forside_file_student_3_name(),
    note: m.forside_file_student_3_note(),
  },
]

/** The full landing page: what AImforA does, how student data is handled, how it works. */
function LandingPage() {
  const { user } = Route.useRouteContext()
  const config = Route.useLoaderData()
  const signedIn = Boolean(user)
  const betaClosed = config.signupMode !== 'open'
  const signupLabel = betaClosed ? m.forside_cta_invited() : m.forside_cta_create()

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <header className="border-line border-b bg-surface">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 sm:px-6">
          <Link to="/" className="mr-auto flex items-center" aria-label={m.forside_home_aria()}>
            <Logo height={24} />
          </Link>
          <Button asChild variant="ghost" size="sm">
            <Link to="/pricing">{m.pricing_title()}</Link>
          </Button>
          {signedIn ? (
            <Button asChild variant="outline" size="sm">
              <Link to="/app">{m.forside_open_app()}</Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm">
                <Link to="/login">{m.landing_login()}</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/signup">{m.forside_cta_create()}</Link>
              </Button>
            </>
          )}
        </div>
      </header>

      <main id="main" className="flex-1">
        <div className="bg-[radial-gradient(ellipse_at_85%_10%,var(--saffron-soft)_0%,transparent_55%)]">
          <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 pt-12 pb-14 sm:px-6 sm:pt-16 lg:grid-cols-[1.05fr_1fr]">
            <div>
              <h1 className="text-balance font-semibold text-[34px] leading-[1.08] tracking-[-0.02em] sm:text-[42px]">
                {m.forside_hero_title_before()}{' '}
                <mark className="marking">{m.forside_hero_title_marked()}</mark>
                {m.forside_hero_title_after()}
              </h1>
              <p className="mt-4 max-w-xl text-[17px] text-ink-soft">{m.forside_hero_body()}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild size="lg">
                  <Link to={signedIn ? '/app' : '/signup'}>
                    {signedIn ? m.forside_cta_to_exams() : signupLabel}
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <a href="#eksempel">{m.forside_cta_example()}</a>
                </Button>
              </div>
              {!signedIn && betaClosed && (
                <p className="mt-4 max-w-xl text-sm">
                  {m.forside_beta_note()}{' '}
                  <Link className="underline underline-offset-2 hover:text-ink" to="/beta">
                    {m.forside_cta_request_beta()}
                  </Link>
                </p>
              )}
            </div>
            <GradingPreview />
          </section>
        </div>

        <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <h2 className="font-semibold text-[24px] tracking-[-0.01em]">{m.forside_safe_title()}</h2>
          <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:items-start">
            <div>
              <h3 className="font-semibold">{m.forside_safe_2_title()}</h3>
              <p className="mt-1 mb-4 max-w-prose text-pretty text-ink-soft text-sm">
                {m.forside_safe_2_body()}
              </p>
              <RedactionFlow />
            </div>
            <dl className="divide-y divide-line border-line border-y">
              {safeguards().map((item) => (
                <div key={item.title} className="py-4">
                  <dt className="font-semibold">{item.title}</dt>
                  <dd className="mt-1 max-w-prose text-pretty text-ink-soft text-sm">
                    {item.body}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="border-line border-y bg-surface">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
            <h2 className="font-semibold text-[24px] tracking-[-0.01em]">
              {m.forside_how_title()}
            </h2>
            <ol className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {steps().map((step, i) => (
                <li key={step.title}>
                  <span className="num inline-flex size-8 items-center justify-center rounded-full bg-saffron font-semibold text-ink text-sm">
                    {i + 1}
                  </span>
                  <h3 className="mt-3 font-semibold">{step.title}</h3>
                  <p className="mt-1 text-pretty text-ink-soft text-sm">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <QuoteCarousel />
        </section>

        <section id="eksempel" className="scroll-mt-4 border-line border-y bg-surface">
          <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_1.1fr]">
            <div>
              <h2 className="font-semibold text-[24px] tracking-[-0.01em]">
                {m.forside_example_title()}
              </h2>
              <p className="mt-3 text-pretty text-ink-soft">{m.forside_example_body()}</p>
              <ol className="mt-6 space-y-3 text-sm">
                {exampleSteps().map((text, i) => (
                  <li key={text} className="flex gap-3">
                    <span className="num w-4 shrink-0 text-ink-soft">{i + 1}.</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ol>
              <p className="mt-6 text-pretty text-ink-soft text-sm">
                {m.forside_example_own_data()}
              </p>
            </div>

            <ul
              className="divide-y divide-line self-start rounded-md border border-line bg-surface"
              aria-label={m.forside_example_files_label()}
            >
              {exampleFiles().map((item) => (
                <li key={item.file} className="flex items-center gap-3 px-4 py-3">
                  <FileTextIcon className="size-4 shrink-0 text-ink-soft" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-sm">{item.name}</span>
                    <span className="block text-[12.5px] text-ink-soft">{item.note}</span>
                  </span>
                  <Button asChild variant="ghost" size="sm">
                    <a
                      href={`/eksempel/${item.file}`}
                      download
                      aria-label={m.forside_example_download_file({ name: item.name })}
                    >
                      <DownloadIcon /> {m.forside_example_download()}
                    </a>
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-saffron/30 border-t bg-saffron-soft">
          <div className="mx-auto max-w-6xl px-4 py-14 text-center sm:px-6">
            <h2 className="font-semibold text-[24px] tracking-[-0.01em]">
              {m.forside_final_title()}
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-pretty text-ink-soft">
              {betaClosed
                ? m.forside_final_body_invited({ count: String(TRIAL_CREDITS) })
                : m.forside_final_body({ count: String(TRIAL_CREDITS) })}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {signedIn ? (
                <Button asChild>
                  <Link to="/app">{m.forside_cta_to_exams()}</Link>
                </Button>
              ) : (
                <>
                  <Button asChild>
                    <Link to="/signup">{signupLabel}</Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/login">{m.landing_login()}</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-line border-t bg-surface">
        <div className="mx-auto max-w-6xl space-y-1 px-4 py-6 text-[13px] text-ink-soft sm:px-6">
          <p>{m.forside_footer()}</p>
          <p>{m.forside_footer_dpa()}</p>
          <LegalLinks className="flex flex-wrap gap-x-4 pt-1" />
        </div>
      </footer>
    </div>
  )
}
